import React, { useState, useEffect, createRef } from 'react';
import Navigator from '../spatial-navigation/navigator/navigator.jsx';
import PropTypes from 'prop-types';
import '../styles.css';
import SecretMenu from '../secret-menu/secret-menu-component.jsx';
import { altair } from '../altair';
import VersionViewer from '../version-viewer/version-viewer.jsx';
import './antares.css';
import { initNavigation } from '../spatial';
import { PerformanceEnhancerContext } from '../performance-enabler/performance-enabler.jsx';
require('es6-object-assign/auto');

/**
 * __Antares__ is the main component that wraps the entire application. 
 * ``` jsx static
 * import React from 'react';
import Antares from 'antares';
const App = () => {
        return (
            <Antares>
                <div>Hello Antares!</div>
            </Antares>
        )
}
```
*/
const Antares = (props) => {
    const [antaresLoaded, setAntaresLoaded] = useState(false);
    const [showSecretMenu, setShowSecretMenu] = useState(false);

    const secretMenuIndex = createRef(0);
    const secretMenuEventListener = createRef(null);

    const federated = createRef(props.federated);

    useEffect(() => {

        console.log('props', props)

        if (props.federated) {
            // launch antares directly
            setAntaresLoaded(true);
            props.onAntaresLoaded();
        } else {
            if (secretMenuEventListener.current === null) {
                window.addEventListener('keydown', secretMenuEvent, false);
                secretMenuEventListener.current = true;
            };
            startup();
        }

    }, []);

    const secretMenuEvent = () => {
        let keys = [];
        let timeout = null;
        //YYGBBR
        if (altair.isHbbTV()) {
            keys = [405, 405, 404, 406, 406, 403];
        } else {
            keys = [89, 89, 71, 66, 66, 82];
        }
        if (event.keyCode === keys[secretMenuIndex.current]) {
            if (secretMenuIndex.current === keys.length - 1) {
                setShowSecretMenu(true);
            } else {
                secretMenuIndex.current = secretMenuIndex.current + 1;
                if (timeout !== null) {
                    clearTimeout(timeout);
                    timeout = null;
                }
                timeout = setTimeout(() => {
                    secretMenuIndex.current = 0;
                    clearTimeout(timeout);
                    timeout = null;
                }, 5000);
            }
        } else {
            secretMenuIndex.current = 0;
            clearTimeout(timeout);
            timeout = null;
        }
    }

    const hideSecretMenu = () => {
        setShowSecretMenu(false);
    }

    const startup = () => {
        if (props.preventHbbTVInit || !altair.isHbbTV()) {
            initNavigation({
                debug: props.spatialDebug,
                visualDebug: props.spatialVisualDebug,
                throttle: props.throttle
            });
            setAntaresLoaded(true);
            props.onAntaresLoaded();
        } else {
            const keySet = props.keySet && Array.isArray(props.keySet) ? props.keySet : [altair.HBBTV_KEYS.RED, altair.HBBTV_KEYS.GREEN, altair.HBBTV_KEYS.YELLOW, altair.HBBTV_KEYS.BLUE, altair.HBBTV_KEYS.NAVIGATION];
            altair.initializeHbbTV({
                bindToChannel: true,
                keySet,
                destryAppOnCatch: props.destryAppOnCatch
            })
                .then(() => {
                    initNavigation({
                        debug: true,
                        visualDebug: props.spatialVisualDebug,
                        throttle: props.throttle
                    });
                    setAntaresLoaded(true);
                    props.onAntaresLoaded();
                })
                .catch(e => console.log(e));
        }
    }

    if (!antaresLoaded) return null;

    return (
        <Navigator>
            <VersionViewer appVersion={props.appVersion} />
            <PerformanceEnhancerContext.Provider value={{ enhancePerformance: props.enhancePerformance }}>
                <div className={'antares-container'}>
                    {props.children}
                </div>
            </PerformanceEnhancerContext.Provider>
            {props.secretMenuEnabled ? (showSecretMenu ? <SecretMenu infos={props.secretMenuInfo} hideSecretMenu={hideSecretMenu} /> : null) : null}
        </Navigator>
    );
}

Antares.propTypes = {
    children: PropTypes.node.isRequired,
    /**
    *  if HbbTV prop is true, then Antares will look for this prop to set the Keys that will be listened in client application.
    */
    keySet: PropTypes.array,
    /**
    *  if this prop is true and if the device is HbbTV, prevent the HbbTV process initialization
    */
    preventHbbTVInit: PropTypes.bool,
    /**
     *  callback that is called after the Antares startup process. Is usefull if there are some operations to perform before the app is shown.
     */
    onAntaresLoaded: PropTypes.func,
    /**
    *  when this property is true, Antares will enable the secret menu listener. To show secret menu, you have to press YYGBBR buttons on remote controller.
    */
    secretMenuEnabled: PropTypes.bool,
    /**
     *  secretMenu informations to be shown as object
     */
    secretMenuInfo: PropTypes.object,
    /**
     *  how much milliseconds Antares had to wait before triggers keydown functions in a "continously pressed" situation.
     */
    throttle: PropTypes.number,
    /**
     * Object with enabled t/f and version. It enables a little square on the top left corner with the version you specify in the arguments.
     */
    appVersion: PropTypes.object,
    /**
    * If this prop is true, the application you are currently building is federated. So Antares no need to be initialized again.
    */
    federated: PropTypes.bool,
    /**
   * If this prop is true, performance CSS enhancement are enabled
   */
    enhancePerformance: PropTypes.bool
};

Antares.defaultProps = {
    withSecretMenu: false,
    preventHbbTVInit: false,
    onAntaresLoaded: () => { console.log('[ANTARES v.2] Framework loaded.'); },
    secretMenuEnabled: false,
    secretMenuInfo: {},
    throttle: 0,
    spatialDebug: false,
    spatialVisualDebug: false,
    destryAppOnCatch: false,
    federated: false,
    enhancePerformance: false
};

export default Antares;